/**
 * Converts matched assets data into expert data.
 *
 * @param {Array} data - The matched assets data to be converted.
 * @return {Array} The converted expert data.
 */
export const convertMachedAssetsToExpertData = (data) => {
  const assetsMap = {};

  const groupedByStrategy = data
    ?.map((item) => {
      return {
        strategy: item.strategy,
        assets: item.results,
      };
    })
    .map((item) => {
      const sortedData = { ...item };

      if (Array.isArray(sortedData.assets)) {
        sortedData.assets = [...sortedData.assets].sort((a, b) => {
          // Handle null cases
          if (a.last_matched_date === null && b.last_matched_date === null) {
            return 0;
          }
          if (a.last_matched_date === null) {
            return 1; // Place nulls at the end
          }
          if (b.last_matched_date === null) {
            return -1; // Place nulls at the end
          }

          // Compare dates (most recent first)
          return new Date(b.last_matched_date) - new Date(a.last_matched_date);
        });
      }

      return sortedData;
    });

  // Check if data is defined and is an array
  if (Array.isArray(data)) {
    // Iterate over the original data
    data.forEach((entry) => {
      const strategy = entry.strategy;

      // Check if entry.results is defined and is an array
      if (Array.isArray(entry.results)) {
        entry.results.forEach((result) => {
          const assetKey = result.asset_key;
          const assetName = result.asset_name;
          const timestamp = result.timestamp;
          const date = result.date;
          const charts = result.charts;
          const last_matched_date = result.last_matched_date;

          // If the asset is not in the map, add it
          if (!assetsMap[assetKey]) {
            assetsMap[assetKey] = {
              asset_key: assetKey,
              asset_name: assetName,
              date,
              indicators: [],
            };
          }

          // Add the strategy to the asset's indicators
          assetsMap[assetKey].indicators.push({
            strategy: strategy,
            report: result.report,
            timestamp: timestamp,
            is_user_seen: result.is_user_seen,
            charts: charts,
            last_matched_date: last_matched_date,
          });
        });
      }
    });
  }

  // Convert the map to an array
  let assetsArray = Object.values(assetsMap);

  // Separate assets with and without date
  const withDate = assetsArray.filter((asset) => asset.date);
  const withoutDate = assetsArray.filter((asset) => !asset.date);

  // Sort assets with a date by the date property
  withDate.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Concatenate sorted withDate array with withoutDate array
  assetsArray = withDate.concat(withoutDate);

  // return assetsArray;
  return groupedByStrategy;
};

/**
 * Sorts matched assets by the last matched date of their indicators.
 *
 * Given two items `a` and `b`, this function first collects all the
 * `last_matched_date` timestamps from their indicators. If an indicator
 * does not have a `last_matched_date`, it is ignored. If either `a` or
 * `b` has no indicators, they are considered to have a maximum date of 0.
 *
 * Then, the function finds the maximum date for each item's indicators.
 *
 * Finally, the function returns the difference between `b`'s maximum date
 * and `a`'s maximum date. This is used to sort the items in descending
 * order of their maximum last matched date (newest first).
 *
 * @param {Object} a - The first item to compare.
 * @param {Object} b - The second item to compare.
 * @return {number} The difference between `b`'s maximum date and `a`'s maximum date.
 */
export const sortMatchedAssetsBaseLastMatchedDate = (a, b) => {
  // Supports both shapes:
  // - advanced/beginner: { indicators: [...] }
  // - expert: { assets: [...] }
  const getMaxDate = (item) => {
    const source = Array.isArray(item?.indicators)
      ? item.indicators
      : Array.isArray(item?.assets)
      ? item.assets
      : [];

    const dates = source
      .map((entry) =>
        entry?.last_matched_date ? new Date(entry.last_matched_date).getTime() : 0
      )
      .filter((value) => Number.isFinite(value));

    return dates.length ? Math.max(...dates) : 0;
  };

  // Find the maximum date for each item
  const aMaxDate = getMaxDate(a);
  const bMaxDate = getMaxDate(b);

  // For newest-first ordering, compare b - a
  return bMaxDate - aMaxDate;
};

/**
 * Filters out timeframes that are already associated with a specific indicator key.
 * @param {Array<Object>} indicators - An array of indicator objects, each containing a key and timeframe.
 * @param {Array<Object>} timeframes - An array of timeframe objects, each containing a key.
 * @param {string} indicatorKey - The key of the indicator to filter timeframes against.
 * @returns {Array<Object>} An array of timeframe objects that are not associated with the specified indicator key.
 */
export const filterIndicatorTimeframes = (
  indicators,
  timeframes,
  indicatorKey
) => {
  const indicatorTimeframes = new Set(
    indicators
      ?.filter((ind) => ind.key === indicatorKey)
      ?.map((ind) => ind.timeframe)
  );

  if (!indicatorTimeframes.size) {
    return timeframes || [];
  }

  const filteredTimeFrames =
    timeframes?.filter((tf) => !indicatorTimeframes.has(tf.key)) || [];

  return filteredTimeFrames;
};
